/**
 * video-editor/compose.js — ОДИН рисовальщик кадра ленты для превью и файла.
 *
 * Зовут двое: превью (источник кадра — <video> или картинка) и экспорт (крючок
 * `hooks.compose` движка VideoMergeWebCodecs, источник — декодированный
 * VideoFrame). Всё, что видно в кадре, решается здесь и только здесь:
 * вписывание (поля / заполнение / размытый фон), поворот, переход между
 * клипами, скрытие областей и надписи. Вторая копия этого кода — тот самый
 * класс «в превью одно, в файле другое» (/audio-enhancer 24.08: «в
 * предпросмотре вау, на выходе ужас» оказалось буквально верным).
 *
 * Размеры — в долях кадра: превью рисует в канву ~960 px, экспорт — в 1920
 * или 4K; одинаковые доли дают одинаковую картинку.
 *
 * window.VideoEditorCompose:
 *   draw(ctx, W, H, t, tl, fr, srcA, srcB, regions, ovSrc)
 *       tl  — результат VideoEditorTimeline.build
 *       fr  — tl.frameAt(t) (может быть null — чёрный кадр с надписями)
 *       srcA/srcB — чем рисовать клип fr.a / fr.b (VideoFrame, <video>, картинка)
 *       ovSrc — { idНаложения: кадр } для видео поверх видео (нет кадра —
 *               наложение в этот момент не рисуется)
 *   drawClip(ctx, src, clip, W, H) — один клип целиком (для миниатюр/проб)
 */
(function () {
'use strict';
if (window.VideoEditorCompose) return;

function VO() { return window.VideoOrientation; }

function srcSize(src) {
    var vo = VO();
    if (vo) return vo.size(src);
    return {
        w: src.displayWidth || src.codedWidth || src.videoWidth || src.naturalWidth || src.width || 0,
        h: src.displayHeight || src.codedHeight || src.videoHeight || src.naturalHeight || src.height || 0,
    };
}

/* Нарисовать источник в прямоугольник кадра с пользовательским поворотом.
   Поворот КОНТЕЙНЕРА уже сидит в кадре (VideoOrientation), пользовательский —
   снаружи; складываются они сами. Отражение — в осях КАДРА (масштаб до
   поворота): «по горизонтали» зеркалит то, что человек видит, при любом
   повороте, а не ось исходника. */
function drawRotated(ctx, src, rot, cx, cy, dw, dh, flipH, flipV) {
    var swap = (rot === 90 || rot === 270);
    ctx.save();
    ctx.translate(cx, cy);
    if (flipH || flipV) ctx.scale(flipH ? -1 : 1, flipV ? -1 : 1);
    if (rot) ctx.rotate(rot * Math.PI / 180);
    var w = swap ? dh : dw, h = swap ? dw : dh;
    var vo = VO();
    if (vo) vo.draw(ctx, src, -w / 2, -h / 2, w, h);
    else ctx.drawImage(src, -w / 2, -h / 2, w, h);
    ctx.restore();
}

function fittedSize(src, rot, W, H, mode) {
    var s = srcSize(src);
    if (!s.w || !s.h) return null;
    var swap = (rot === 90 || rot === 270);
    var ew = swap ? s.h : s.w, eh = swap ? s.w : s.h;
    var k = (mode === 'cover') ? Math.max(W / ew, H / eh) : Math.min(W / ew, H / eh);
    return { w: ew * k, h: eh * k };
}

/* ── размытый фон ─────────────────────────────────────────────────────────
   Вертикальное видео в горизонтальном кадре (и наоборот): вместо чёрных полей
   — тот же кадр, растянутый на весь кадр и размытый. Размытие — через
   уменьшение в крошечную канву и обратное растяжение со сглаживанием, а не
   `ctx.filter`: фильтр канвы на iOS < 18 не работает вовсе (прокладка
   CanvasCssFilter дорогая для каждого кадра), а уменьшение одинаково везде —
   значит превью и файл совпадают на любом устройстве. Крошечная канва
   масштабируется от короткой стороны КАДРА, поэтому размытие выглядит
   одинаково в превью 960 px и в файле 4K. */
var BLUR_SHORT = 18;          // короткая сторона крошечной канвы, px
var scratch = {};
function scratchCanvas(key, w, h) {
    var c = scratch[key];
    if (!c) {
        c = (typeof OffscreenCanvas !== 'undefined') ? new OffscreenCanvas(w, h) : document.createElement('canvas');
        scratch[key] = c;
    }
    if (c.width !== w) c.width = w;
    if (c.height !== h) c.height = h;
    return c;
}
function drawBlurFill(ctx, src, rot, W, H, flipH, flipV) {
    var cover = fittedSize(src, rot, W, H, 'cover');
    if (!cover) return;
    var k = BLUR_SHORT / Math.min(W, H);
    var tw = Math.max(2, Math.round(W * k)), th = Math.max(2, Math.round(H * k));
    // Два шага вниз: сразу в 18 px билинейное уменьшение берёт лишь несколько
    // пикселей источника, и фон мерцает от кадра к кадру. Через промежуточные
    // ×8 он усредняется честно.
    var mw = tw * 8, mh = th * 8;
    var mid = scratchCanvas('mid', mw, mh), mc = mid.getContext('2d');
    mc.imageSmoothingEnabled = true;
    try { mc.imageSmoothingQuality = 'high'; } catch (e) {}
    mc.clearRect(0, 0, mw, mh);
    drawRotated(mc, src, rot, mw / 2, mh / 2, cover.w * (mw / W), cover.h * (mh / H), flipH, flipV);
    var tiny = scratchCanvas('tiny', tw, th), tc = tiny.getContext('2d');
    tc.imageSmoothingEnabled = true;
    try { tc.imageSmoothingQuality = 'high'; } catch (e) {}
    tc.clearRect(0, 0, tw, th);
    tc.drawImage(mid, 0, 0, tw, th);
    ctx.save();
    ctx.imageSmoothingEnabled = true;
    try { ctx.imageSmoothingQuality = 'high'; } catch (e) {}
    ctx.drawImage(tiny, 0, 0, W, H);
    // Притемнить: резкий кадр поверх яркого пятна читается хуже.
    ctx.fillStyle = 'rgba(0,0,0,0.28)';
    ctx.fillRect(0, 0, W, H);
    ctx.restore();
}

/* Цвет клипа — CSS-фильтром канвы: фильтр из общего набора SlideshowFilters
   (тонировки «тепло», «ч/б», «винтаж» — те же строки, что у /slideshow-maker)
   и ручные яркость, контраст, насыщенность. На iOS < 18 фильтр канвы
   работает через общую прокладку (canvas-css-filter.js, и для OffscreenCanvas
   тоже), так что превью и файл красятся одинаково и там. */
function lookFilter(clip) {
    var parts = [];
    var SF = window.SlideshowFilters;
    var tone = clip.tone || 'none';
    if (tone !== 'none' && SF && typeof SF.css === 'function') {
        var pre = SF.css(tone);
        if (pre) parts.push(pre);
    }
    var b = clip.bright || 1, c = clip.contrast || 1, s = clip.sat == null ? 1 : clip.sat;
    if (!(Math.abs(b - 1) < 1e-3 && Math.abs(c - 1) < 1e-3 && Math.abs(s - 1) < 1e-3)) {
        parts.push('brightness(' + b.toFixed(3) + ') contrast(' + c.toFixed(3) + ') saturate(' + s.toFixed(3) + ')');
    }
    return parts.join(' ');
}
/* Движение фото: масштаб от 1 до MOTION_ZOOM (или обратно) за время показа,
   ровно по доле показа — поэтому кадр N файла и превью в тот же момент
   совпадают. После разреза половина играет свою долю хода (motionSpan). */
var MOTION_ZOOM = 1.12;
function motionScale(clip, p) {
    if (!clip || clip.kind !== 'image' || !clip.motion || clip.motion === 'none') return 1;
    p = p < 0 ? 0 : (p > 1 ? 1 : p);
    var ms = clip.motionSpan;
    if (ms && ms.length === 2) p = ms[0] + (ms[1] - ms[0]) * p;
    // сглаженный ход: без рывка в начале и в конце показа
    var e = p * p * (3 - 2 * p);
    return clip.motion === 'out' ? MOTION_ZOOM - (MOTION_ZOOM - 1) * e : 1 + (MOTION_ZOOM - 1) * e;
}

/* Титульный кадр: заливка от color к color2 по диагонали. Совсем ровная
   заливка кодируется в килобайты, и общий кодировщик честно отвечает
   `output-starved` (кормили пустыми кадрами), — поэтому интерфейс даёт
   «сплошному» цвету второй оттенок на несколько процентов темнее. */
function drawCard(ctx, clip, W, H) {
    var g = ctx.createLinearGradient(0, 0, W * 0.35, H);
    g.addColorStop(0, clip.color || '#1e293b');
    g.addColorStop(1, clip.color2 || clip.color || '#0f172a');
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, W, H);
}

/** Сколько картинки клипа выходит за кадр (доли кадра) — столько можно сдвигать. */
function overflowOf(src, clip, W, H) {
    var rot = clip.rotation || 0;
    var sz = fittedSize(src, rot, W, H, clip.fit === 'cover' ? 'cover' : 'contain');
    if (!sz) return null;
    var k = clip.zoom || 1;
    return { x: Math.max(0, sz.w * k / W - 1), y: Math.max(0, sz.h * k / H - 1) };
}

/**
 * Один клип на весь кадр: фон, вписывание, поворот, отражение, масштаб,
 * цвет, движение фото. p — доля показа клипа (0…1), нужна только движению.
 */
function drawClip(ctx, src, clip, W, H, p) {
    // Поля «целиком» — своим цветом; у размытого фона и «заполнить» полей нет.
    ctx.fillStyle = (clip && clip.fit === 'contain' && clip.kind !== 'color' && clip.bgColor) ? clip.bgColor : '#000';
    ctx.fillRect(0, 0, W, H);
    if (!clip) return;
    if (clip.kind === 'color') { drawCard(ctx, clip, W, H); return; }
    if (!src) return;
    var rot = clip.rotation || 0;
    var mode = clip.fit === 'cover' ? 'cover' : 'contain';
    var f = lookFilter(clip);
    if (f) ctx.filter = f;
    try {
        if (clip.fit === 'blur') drawBlurFill(ctx, src, rot, W, H, clip.flipH, clip.flipV);
        var sz = fittedSize(src, rot, W, H, mode);
        if (!sz) return;
        var k = motionScale(clip, p || 0) * (clip.zoom || 1);
        var dw = sz.w * k, dh = sz.h * k;
        // Кадрирование: кадр, который шире (выше) рамки, сдвинут на выбранную
        // долю лишнего — у горизонтального ролика в 9:16 человек сам решает,
        // кто из героев в кадре, а не всегда середина.
        var ox = Math.max(0, dw - W) / 2 * (clip.panX || 0), oy = Math.max(0, dh - H) / 2 * (clip.panY || 0);
        drawRotated(ctx, src, rot, W / 2 - ox, H / 2 - oy, dw, dh, clip.flipH, clip.flipV);
    } finally {
        if (f) ctx.filter = 'none';
    }
}
function progOf(tl, en) {
    if (!tl || !en) return 0;
    var d = tl.effDur ? tl.effDur[en.index] : 0;
    return d > 0 ? en.localSec / d : 0;
}
/** Появление из чёрного и уход в чёрный: a — яркость 0…1 (модель: clipFadeAt). */
function dim(ctx, W, H, a) {
    if (!(a < 0.999)) return;
    ctx.save();
    ctx.globalAlpha = Math.min(1, Math.max(0, 1 - a));
    ctx.fillStyle = '#000';
    ctx.fillRect(0, 0, W, H);
    ctx.restore();
}
function fadeOf(tl, en, t) {
    return (tl && en && typeof tl.clipFadeAt === 'function') ? tl.clipFadeAt(en.index, t) : 1;
}

/* ── переходы ─────────────────────────────────────────────────────────────
   Оба клипа сперва собираются ЦЕЛИКОМ (со своим фоном и уходом в чёрный) в
   отдельных канвах, и уже они ложатся в кадр — прозрачностью, маской,
   сдвигом или масштабом. Рисовать клип прямо в кадр под globalAlpha нельзя:
   заливка фона тоже становится полупрозрачной и притемняет соседа — наплыв
   проваливался до 75 % яркости посередине (так рисует drawClipFrame движка
   /video-merge: заливка чёрным перед кадром).

   Направление в названии — направление ДВИЖЕНИЯ: «шторка влево» — граница
   едет справа налево, «сдвиг влево» — кадр уезжает влево, новый приходит
   справа. Композитор движка понимал «wipeleft» наоборот (новый кадр рос
   слева), и надпись на кнопке расходилась с тем, что видно. */
function ease(p) { p = p < 0 ? 0 : (p > 1 ? 1 : p); return p * p * (3 - 2 * p); }
function scaled(ctx, img, W, H, s) {
    var w = W * s, h = H * s;
    ctx.drawImage(img, (W - w) / 2, (H - h) / 2, w, h);
}
function transition(ctx, A, B, p, type, W, H) {
    var e = ease(p);
    ctx.save();
    switch (type) {
        case 'fadeblack':
        case 'fadewhite':
            ctx.fillStyle = type === 'fadewhite' ? '#fff' : '#000';
            if (p < 0.5) { ctx.drawImage(A, 0, 0, W, H); ctx.globalAlpha = p * 2; ctx.fillRect(0, 0, W, H); }
            else { ctx.fillRect(0, 0, W, H); ctx.globalAlpha = (p - 0.5) * 2; ctx.drawImage(B, 0, 0, W, H); }
            break;
        case 'wipeleft': case 'wiperight': case 'wipeup': case 'wipedown':
            ctx.drawImage(A, 0, 0, W, H);
            ctx.beginPath();
            if (type === 'wipeleft') ctx.rect(W * (1 - p), 0, W * p, H);
            else if (type === 'wiperight') ctx.rect(0, 0, W * p, H);
            else if (type === 'wipeup') ctx.rect(0, H * (1 - p), W, H * p);
            else ctx.rect(0, 0, W, H * p);
            ctx.clip();
            ctx.drawImage(B, 0, 0, W, H);
            break;
        case 'slideleft': case 'slideright': case 'slideup': case 'slidedown': {
            var dx = type === 'slideleft' ? -1 : (type === 'slideright' ? 1 : 0);
            var dy = type === 'slideup' ? -1 : (type === 'slidedown' ? 1 : 0);
            ctx.drawImage(A, dx * W * e, dy * H * e, W, H);
            ctx.drawImage(B, -dx * W * (1 - e), -dy * H * (1 - e), W, H);
            break;
        }
        case 'circleopen':
        case 'circleclose': {
            var maxR = Math.sqrt(W * W + H * H) / 2;
            var open = type === 'circleopen';
            ctx.drawImage(open ? A : B, 0, 0, W, H);
            ctx.beginPath();
            ctx.arc(W / 2, H / 2, Math.max(0, maxR * (open ? e : 1 - e)), 0, Math.PI * 2);
            ctx.clip();
            ctx.drawImage(open ? B : A, 0, 0, W, H);
            break;
        }
        case 'zoomin':
            // Камера «въезжает» в уходящий кадр, он растворяется в новом.
            ctx.drawImage(B, 0, 0, W, H);
            ctx.globalAlpha = 1 - e;
            scaled(ctx, A, W, H, 1 + 0.45 * e);
            break;
        case 'zoomout':
            // Новый кадр «отъезжает» от зрителя на своё место.
            ctx.drawImage(A, 0, 0, W, H);
            ctx.globalAlpha = e;
            scaled(ctx, B, W, H, 1.45 - 0.45 * e);
            break;
        case 'blur': {
            // Уходящий кадр расплывается, новый собирается из размытого.
            var R = Math.max(2, Math.round(Math.min(W, H) * 0.025));
            var ra = Math.round(R * Math.min(1, p * 2)), rb = Math.round(R * Math.min(1, (1 - p) * 2));
            if (ra > 0) ctx.filter = 'blur(' + ra + 'px)';
            ctx.drawImage(A, 0, 0, W, H);
            ctx.filter = rb > 0 ? 'blur(' + rb + 'px)' : 'none';
            ctx.globalAlpha = e;
            ctx.drawImage(B, 0, 0, W, H);
            ctx.filter = 'none';
            break;
        }
        default:
            // Наплыв (и любой незнакомый тип: модель сводит его к наплыву).
            ctx.drawImage(A, 0, 0, W, H);
            ctx.globalAlpha = p;
            ctx.drawImage(B, 0, 0, W, H);
    }
    ctx.restore();
}

/* ── наложения (логотип, стикер, картинка или видео поверх видео) ─────────
   Картинку наложения отдаёт тот, кто знает файлы (app.js → media), через
   setImageResolver: рисовальщик живёт и в превью, и в экспорте ОДНОЙ
   страницы, поэтому один и тот же битмап рисуется в оба места. Кадр
   ВИДЕО-наложения у каждого свой и на каждый момент: превью отдаёт свой
   <video>, экспорт — декодированный кадр (ovSrc[id] в draw). Геометрию
   (пропорции) видео по-прежнему знает resolver: у записи файла есть
   <video> с размерами кадра, даже пока нужный кадр ещё ищется. */
var imageOf = null;

/* Хромакей в плоскости цветности (Cb, Cr): расстояние пикселя до цвета фона.
   Ближе sim — прозрачно, дальше sim+smooth — как есть, между ними — мягкий
   край; в полосе sim+spill ещё и гасится отсвет фона (цвет уходит к серому),
   иначе волосы и плечи остаются в зелёной кайме. Так устроен и хромакей
   OBS; расстояние по цветности, а не по RGB, не путает тёмный фон со
   светлым — тень на зелёном экране остаётся зелёной. */
function hexRgb(h) {
    var n = parseInt(String(h || '#00ff00').slice(1), 16);
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}
function chromaKey(data, key) {
    var kc = hexRgb(key.color);
    var kb = (-0.168736 * kc[0] - 0.331264 * kc[1] + 0.5 * kc[2]) / 255;
    var kr = (0.5 * kc[0] - 0.418688 * kc[1] - 0.081312 * kc[2]) / 255;
    var sim = key.sim, sm = Math.max(1e-4, key.smooth), sp = Math.max(1e-4, key.spill);
    var far = sim + Math.max(sm, sp), far2 = far * far;
    for (var i = 0; i < data.length; i += 4) {
        var r = data[i], g = data[i + 1], b = data[i + 2];
        var db = (-0.168736 * r - 0.331264 * g + 0.5 * b) / 255 - kb;
        var dr = (0.5 * r - 0.418688 * g - 0.081312 * b) / 255 - kr;
        var d2 = db * db + dr * dr;
        if (d2 >= far2) continue;                       // далеко от фона — как есть
        var base = Math.sqrt(d2) - sim;
        var m = base <= 0 ? 0 : Math.min(1, base / sm);
        data[i + 3] = Math.round(data[i + 3] * m * Math.sqrt(m));
        var s = base <= 0 ? 0 : Math.min(1, base / sp);
        s = s * Math.sqrt(s);
        if (s < 1) {
            var l = 0.2126 * r + 0.7152 * g + 0.0722 * b;
            data[i] = l + (r - l) * s; data[i + 1] = l + (g - l) * s; data[i + 2] = l + (b - l) * s;
        }
    }
}
var keyCanvas = null, keyCtx = null;
/* Попиксельный проход — на процессоре, поэтому размер ограничен: наложение
   шире 1920 px в кадре всё равно упирается в шов разрешения источника. */
var KEY_MAX = 1920;
function keyedImage(src, key, w, h, crop) {
    w = Math.max(2, Math.round(w)); h = Math.max(2, Math.round(h));
    if (w > KEY_MAX) { h = Math.max(2, Math.round(h * KEY_MAX / w)); w = KEY_MAX; }
    if (!keyCanvas) {
        keyCanvas = (typeof OffscreenCanvas !== 'undefined') ? new OffscreenCanvas(w, h) : document.createElement('canvas');
        keyCtx = keyCanvas.getContext('2d', { willReadFrequently: true });
    }
    if (keyCanvas.width !== w) keyCanvas.width = w;
    if (keyCanvas.height !== h) keyCanvas.height = h;
    keyCtx.clearRect(0, 0, w, h);
    drawCropped(keyCtx, src, 0, 0, w, h, crop);
    var im = keyCtx.getImageData(0, 0, w, h);
    chromaKey(im.data, key);
    keyCtx.putImageData(im, 0, 0);
    return keyCanvas;
}
/* Источник в прямоугольник: crop — вырезка источника в display-координатах
   (круг вырезает середину кадра под квадрат), иначе целиком. */
function drawCropped(ctx, src, dx, dy, dw, dh, crop) {
    var vo = VO();
    if (crop) {
        if (vo) vo.drawSource(ctx, src, crop.x, crop.y, crop.w, crop.h, dx, dy, dw, dh);
        else ctx.drawImage(src, crop.x, crop.y, crop.w, crop.h, dx, dy, dw, dh);
    } else if (vo) vo.draw(ctx, src, dx, dy, dw, dh);
    else ctx.drawImage(src, dx, dy, dw, dh);
}
function shapePath(ctx, shape, x, y, w, h) {
    ctx.beginPath();
    if (shape === 'circle') { ctx.ellipse(x + w / 2, y + h / 2, w / 2, h / 2, 0, 0, Math.PI * 2); return; }
    var r = Math.min(w, h) * 0.12;
    if (typeof ctx.roundRect === 'function') { ctx.roundRect(x, y, w, h, r); return; }
    ctx.moveTo(x + r, y);
    ctx.arcTo(x + w, y, x + w, y + h, r); ctx.arcTo(x + w, y + h, x, y + h, r);
    ctx.arcTo(x, y + h, x, y, r); ctx.arcTo(x, y, x + w, y, r);
    ctx.closePath();
}
function drawOverlays(ctx, list, W, H, ovSrc) {
    if (!list || !list.length) return;
    for (var i = 0; i < list.length; i++) {
        var v = list[i], it = v.item;
        var img = it.kind === 'video' ? (ovSrc ? ovSrc[it.id] : null) : (imageOf ? imageOf(it.mediaId) : null);
        if (!img || !(v.alpha > 0)) continue;
        var a = Math.max(0, Math.min(1, v.alpha * (it.opacity == null ? 1 : it.opacity)));
        if (!(a > 0.001)) continue;
        var b = overlayBox(it, W, H, img);
        var crop = it.shape === 'circle' ? squareCrop(img) : null;
        ctx.save();
        ctx.globalAlpha = a;
        ctx.translate(b.x + b.w / 2, b.y + b.h / 2);
        if (v.scale && v.scale !== 1) ctx.scale(v.scale, v.scale);
        var x0 = -b.w / 2, y0 = -b.h / 2;
        if (it.shape === 'round' || it.shape === 'circle') { ctx.save(); shapePath(ctx, it.shape, x0, y0, b.w, b.h); ctx.clip(); }
        if (it.chroma) ctx.drawImage(keyedImage(img, it.chroma, b.w * (v.scale || 1), b.h * (v.scale || 1), crop), x0, y0, b.w, b.h);
        else drawCropped(ctx, img, x0, y0, b.w, b.h, crop);
        if (it.shape === 'round' || it.shape === 'circle') ctx.restore();
        if (it.border === 'white' || it.border === 'black') {
            ctx.lineWidth = Math.max(2, Math.min(b.w, b.h) * 0.03);
            ctx.strokeStyle = it.border === 'white' ? '#ffffff' : '#000000';
            if (it.shape === 'rect') { ctx.beginPath(); ctx.rect(x0, y0, b.w, b.h); } else shapePath(ctx, it.shape, x0, y0, b.w, b.h);
            ctx.stroke();
        }
        ctx.restore();
    }
}
/** Середина кадра под квадрат (круглое наложение), в display-координатах. */
function squareCrop(img) {
    var s = srcSize(img);
    if (!s.w || !s.h) return null;
    var m = Math.min(s.w, s.h);
    return { x: (s.w - m) / 2, y: (s.h - m) / 2, w: m, h: m };
}
/**
 * Геометрия наложения в кадре (рисование, рамка выделения, попадание мышью).
 * Пропорции — от display-размера источника (поворот телефона учтён); круг —
 * квадрат со стороной ширины.
 */
function overlayBox(it, W, H, img) {
    img = img || (imageOf ? imageOf(it.mediaId) : null);
    var s = img ? srcSize(img) : { w: 0, h: 0 };
    var iw = s.w || 1, ih = s.h || 1;
    var w = it.w * W, h = it.shape === 'circle' ? w : w * ih / iw;
    return { x: it.x * W - w / 2, y: it.y * H - h / 2, w: w, h: h };
}

/**
 * Кадр ленты в момент t.
 * Порядок слоёв обязателен: клипы (с переходом) → скрытие → наложения →
 * надписи. Скрытие прячет то, что СНЯТО; логотип и подпись — то, что автор
 * положил поверх, — оно не пикселизуется вместе с номером, а прозрачные
 * места логотипа показывают уже скрытый кадр.
 */
function draw(ctx, W, H, t, tl, fr, srcA, srcB, regions, ovSrc) {
    if (!fr) {
        ctx.fillStyle = '#000';
        ctx.fillRect(0, 0, W, H);
    } else if (!fr.b) {
        drawClip(ctx, srcA, fr.a.clip, W, H, progOf(tl, fr.a));
        dim(ctx, W, H, fadeOf(tl, fr.a, t));
    } else {
        var ac = scratchCanvas('A', W, H), ax = ac.getContext('2d');
        drawClip(ax, srcA, fr.a.clip, W, H, progOf(tl, fr.a));
        dim(ax, W, H, fadeOf(tl, fr.a, t));
        var bc = scratchCanvas('B', W, H), bx = bc.getContext('2d');
        drawClip(bx, srcB, fr.b.clip, W, H, progOf(tl, fr.b));
        dim(bx, W, H, fadeOf(tl, fr.b, t));
        transition(ctx, ac, bc, fr.prog, fr.type || 'fade', W, H);
    }
    var RD = window.VideoEditorRedact;
    if (RD && regions && regions.length) RD.apply(ctx, RD.at(regions, t), W, H);
    if (tl && typeof tl.overlaysAt === 'function') drawOverlays(ctx, tl.overlaysAt(t), W, H, ovSrc);
    var OV = window.VideoEditorOverlay;
    if (OV && tl) OV.draw(ctx, tl.textsAt(t), W, H);
}

window.VideoEditorCompose = { draw: draw, drawClip: drawClip, lookFilter: lookFilter, motionScale: motionScale,
    overflowOf: overflowOf, overlayBox: overlayBox, transition: transition, chromaKey: chromaKey,
    setImageResolver: function (fn) { imageOf = typeof fn === 'function' ? fn : null; },
    _drawBlurFill: drawBlurFill };
})();
